/**
 * El courier de Lima (20260925169000): su perfil, sus motorizados y la asignación.
 *
 * ⭐ TODO PASA POR FUNCIONES DE LA BASE, como el resto de los actores: cada una verifica quién llama.
 *    El negocio que vende elige el courier; el courier elige el motorizado.
 *
 * ⭐ EL COURIER TRABAJA CON SMARTY DROP, NO CON CADA NEGOCIO (20260925190000). El founder: «esa
 *    invitación se gestiona entre Smarty y el courier; todos los demás actores aceptan las opciones
 *    que tenga la plataforma». Smarty lo activa (`cambiarEnSmartyDrop`); los negocios lo eligen.
 */
import { supabase } from '@/lib/supabase';
import { normalizarTransportista } from '@/lib/operations/carrierLogo';
import { laPlataCambio } from '@/lib/dropshipping/laPlataCambio';
import { errorDeEdgeFunction } from '@/lib/errorDeEdgeFunction';
import { CLAVES_DROP } from '@/lib/dropshipping/clavesDeCache';
import { esGsg } from '@/lib/transporte/esGsg';
import type { DiaDelLibro, DiaPorRendir, VentaDelDia, VentaDelLibro } from '@/lib/dropshipping/laRendicionPorDia';
import type { PagoDelCourier } from '@/lib/dropshipping/laLiquidacionDelCourier';
import type { QueryClient } from '@tanstack/react-query';

export interface PerfilDeCourier {
  nombre: string;
  whatsapp: string | null;
  /** La tarifa base: la que vale mientras no configure sus zonas. */
  tarifa: number;
  activo: boolean;
  /** Si Smarty ya lo activó en Smarty Drop: recién ahí le asignan pedidos. */
  en_smarty_drop: boolean;
  /** Su fila en el catálogo de couriers (20260925176000): de ahí cuelgan sus zonas. */
  logistics_courier_id: string | null;
  /** Cuántas zonas activas configuró. */
  zonas: number;
}

export interface Motorizado {
  id: string;
  nombre: string;
  telefono: string;
  activo: boolean;
}

export async function miPerfilDeCourier(tenantId: string): Promise<PerfilDeCourier | null> {
  const { data, error } = await supabase.rpc('fn_courier_mi_perfil', { p_tenant: tenantId });
  if (error) throw error;
  const fila = ((data ?? []) as PerfilDeCourier[])[0];
  return fila ? { ...fila, tarifa: Number(fila.tarifa) } : null;
}

export async function guardarPerfilDeCourier(tenantId: string, p: { nombre: string; whatsapp: string; tarifa: number }): Promise<void> {
  const { error } = await supabase.rpc('fn_courier_guardar_perfil', {
    p_tenant: tenantId, p_nombre: p.nombre, p_whatsapp: p.whatsapp, p_tarifa: p.tarifa,
  });
  if (error) throw error;
}

export async function misMotorizados(courierId: string): Promise<Motorizado[]> {
  const { data, error } = await supabase.rpc('fn_courier_mis_motorizados', { p_courier: courierId });
  if (error) throw error;
  return (data ?? []) as Motorizado[];
}

export async function guardarMotorizado(courierId: string, m: { id?: string | null; nombre: string; telefono: string; activo?: boolean }): Promise<void> {
  const { error } = await supabase.rpc('fn_courier_guardar_motorizado', {
    p_courier: courierId, p_id: m.id ?? null, p_nombre: m.nombre, p_telefono: m.telefono, p_activo: m.activo ?? true,
  });
  if (error) throw error;
}

/**
 * Los logos que los couriers cargaron en su página de Transporte (el de un courier con cuenta, como
 * Motos Express), por nombre normalizado. Los de Shalom, Olva y demás ya vienen con la app.
 */
export async function logosDeCouriers(): Promise<Record<string, string>> {
  const { data, error } = await supabase.from('logistics_couriers').select('name, logo_url').not('logo_url', 'is', null);
  if (error) throw error;
  const logos: Record<string, string> = {};
  for (const c of (data ?? []) as { name: string; logo_url: string }[]) logos[normalizarTransportista(c.name)] = c.logo_url;
  return logos;
}

/** Si el courier trabaja con Smarty Drop (lo ve la plataforma; la ficha del courier lo muestra). */
/**
 * Cómo opera un courier sin API de Smarty Drop (20261012231500): hasta qué hora sale el mismo día, qué días reparte y
 * qué puede hacer el cliente al recibir. Lo fija Smarty en la ficha del courier; de acá salen los días y las aperturas
 * que ve el drop en la venta. null: no es un courier de Smarty Drop (o la base todavía no lo tiene).
 */
export interface OperacionDelCourier {
  /** La cuenta del courier (`drop_couriers.tenant_id`). */
  courier: string;
  /** «11:00», o null si no promete el mismo día. */
  corte: string | null;
  /** ISO: 1 = lunes … 7 = domingo. */
  dias: number[];
  aperturas: string[];
  predeterminada: string;
}

export async function operacionDelCourier(catalogoId: string): Promise<OperacionDelCourier | null> {
  // `*`: las columnas llegan con su migración, que puede aplicarse después de publicar el panel.
  const { data, error } = await supabase.from('drop_couriers').select('*').eq('logistics_courier_id', catalogoId).maybeSingle();
  if (error) throw error;
  const k = data as { tenant_id: string; corte_mismo_dia?: string | null; dias_de_reparto?: number[]; aperturas?: string[]; apertura_predeterminada?: string } | null;
  if (!k || !k.dias_de_reparto) return null;
  return {
    courier: k.tenant_id,
    corte: k.corte_mismo_dia ? k.corte_mismo_dia.slice(0, 5) : null,
    dias: k.dias_de_reparto,
    aperturas: k.aperturas ?? ['SEALED'],
    predeterminada: k.apertura_predeterminada ?? 'SEALED',
  };
}

export async function fijarOperacionDelCourier(op: OperacionDelCourier): Promise<void> {
  const { error } = await supabase.rpc('fn_courier_fijar_operacion', {
    p_courier: op.courier, p_corte: op.corte, p_dias: op.dias, p_aperturas: op.aperturas, p_predeterminada: op.predeterminada,
  });
  if (error) throw error;
}

export async function estaEnSmartyDrop(catalogoId: string): Promise<boolean> {
  const { data, error } = await supabase.from('logistics_couriers').select('en_smarty_drop').eq('id', catalogoId).maybeSingle();
  if (error) throw error;
  return !!(data as { en_smarty_drop?: boolean } | null)?.en_smarty_drop;
}

/** Smarty activa (o pausa) un courier en Smarty Drop, desde su Transporte. */
export async function cambiarEnSmartyDrop(catalogoId: string, si: boolean): Promise<void> {
  const { error } = await supabase.rpc('fn_courier_en_smarty_drop', { p_catalogo: catalogoId, p_si: si });
  if (error) throw error;
}

export async function asignarCourier(orderId: string, courierId: string): Promise<void> {
  const { error } = await supabase.rpc('fn_pedido_asignar_courier', { p_order: orderId, p_courier: courierId });
  if (error) throw error;
}

/**
 * El drop registra su envío en el courier (GSG) sin esperar a que el almacén lo prepare (20260930100000).
 * Lo pone en la misma cola que el registro automático; apretar dos veces no duplica (`ya_estaba`).
 */
export async function registrarEnElCourier(ventaId: string): Promise<{ estado: string; ya_estaba: boolean; se_registra: string | null }> {
  const { data, error } = await supabase.rpc('fn_courier_registrar_ahora', { p_venta: ventaId });
  if (error) throw error;
  return data as { estado: string; ya_estaba: boolean; se_registra: string | null };
}

/** Cómo paga el cliente al recibir: lo cobra el courier (GSG) y se lo liquida a Smarty. */
export type CobroEnPuerta = 'EFECTIVO' | 'YAPE' | 'PLIN' | 'TARJETA' | 'TRANSFERENCIA';

export interface EntregaElegible {
  /** Los días que se pueden elegir (`YYYY-MM-DD` de Lima), con su etiqueta («Hoy», «Mañana», «sáb, 4/10»). */
  fechas: { fecha: string; etiqueta: string }[];
  /** Los días de reparto del courier en ese distrito, tal como los trae su catálogo. */
  dias: string | null;
  entregar_el: string | null;
  cobro_en_puerta: CobroEnPuerta | null;
  /** Cómo va el registro en el courier (20260930120000); null = todavía no se pidió. */
  registro: EstadoDelRegistroEnElCourier | null;
  /** Lo que impide registrar, en palabras para la pantalla; vacío = listo. */
  faltan: string[];
  /** Cuándo saldría a registrarse si se pide ahora (ISO): ya, o la víspera del día elegido a las 18:00. */
  se_registra: string | null;
  /** La comisión del POS que asume quien vende, en % (`drop_tarifas.comision_pos_pct`); null si no vino. */
  comision_pos_pct: number | null;
  /** Si el cliente puede abrir el paquete (20261001120000): la elegida (null = la predeterminada del courier). */
  apertura: string | null;
  /** Las opciones con los textos del catálogo de GSG, y la que GSG aplica si no se elige. */
  aperturas: OpcionDeApertura[];
  apertura_predeterminada: string;
  /** Hasta qué hora sale el mismo día («9:00», «11:00»); null si el courier no promete el mismo día. */
  corte: string | null;
  /** Cuándo recoge en el almacén («de 10 a 12»), si se sabe. */
  recojo: string | null;
  /** La franja en que entrega («entre las 2 y las 8 pm»), si se sabe. */
  franja: string | null;
}

/**
 * De dónde salen el día, el cobro y la apertura de una venta con courier de Smarty Drop:
 *   · 'api'    — GSG: de su catálogo, por la función `gsg` (corte, días por distrito, aperturas que publica).
 *   · 'sin_api' — North Express y los que operan sin API: de su operación en la base (20261012231500), que
 *                 fija Smarty en la ficha del courier.
 * null: no es un courier de Smarty Drop (agencia, o un transportista que se escribe a mano).
 */
export type ViaDeLaEntrega = 'api' | 'sin_api';

export function viaDeLaEntrega(carrier: string | null | undefined, courierTenantId: string | null | undefined): ViaDeLaEntrega | null {
  // Sin courier asignado por Smarty Drop no hay de dónde sacarlas (ni GSG: sin su cuenta, no es su envío).
  if (!courierTenantId || !carrier?.trim() || /shalom|olva/i.test(carrier)) return null;
  return esGsg(carrier) ? 'api' : 'sin_api';
}

/**
 * Las tres aperturas, con los textos que publica GSG (SEALED, INSPECT, TRY_ON). Un courier sin API ofrece las que
 * fija Smarty en su operación; la espera del motorizado no se conoce, así que no se promete (0).
 */
export const CATALOGO_DE_APERTURAS: OpcionDeApertura[] = [
  { codigo: 'SEALED', nombre: 'No abrir', descripcion: 'El cliente recibe el paquete cerrado. Sólo puede revisar el estado exterior antes de recibirlo.', espera_min: 0 },
  { codigo: 'INSPECT', nombre: 'Puede revisar', descripcion: 'El cliente puede abrir el paquete y revisar el producto antes de recibirlo o pagar, sin usarlo.', espera_min: 0 },
  { codigo: 'TRY_ON', nombre: 'Puede probar', descripcion: 'El cliente puede abrir el paquete y probarse el producto (ropa, calzado) antes de recibirlo o pagar.', espera_min: 0 },
];

/** Una opción de apertura del paquete, con los textos que publica GSG (SEALED, INSPECT, TRY_ON). */
export interface OpcionDeApertura {
  codigo: string;
  nombre: string;
  descripcion: string;
  /** Cuánto espera el motorizado mientras el cliente revisa o prueba. */
  espera_min: number;
}

/** Espejo de `EstadoDelRegistro` (supabase/functions/_shared/gsgRegistro.ts). */
export interface EstadoDelRegistroEnElCourier {
  estado: 'programado' | 'registrando' | 'registrado' | 'fallido' | 'anulando';
  se_registra: string | null;
  /** Lo que se le mandó al courier: el día de salida (`YYYY-MM-DD`) y el medio de pago. */
  salida: string | null;
  pago: string | null;
  error: string | null;
  /** El link de seguimiento que dio el courier al registrar, para el cliente. */
  enlace: string | null;
  /** La apertura que se le mandó, o null: la predeterminada del courier. */
  apertura: string | null;
  /** El courier lo recibió después de su hora límite: puede despacharlo al día hábil siguiente. */
  fuera_de_hora: boolean;
}

/** Los días que el drop puede elegir para la entrega por GSG, con lo ya elegido (función `gsg`, acción `fechas`). */
/** GSG entrega de 2 a 8 de la tarde (GSG, 30-sep: «recojo de 10 a 12, entregas de 2 a 8»). */
export const FRANJA_DE_ENTREGA = 'entre las 2 y las 8 pm';

/** `2026-10-17` → «Hoy», «Mañana» o «sáb, 17/10», como la etiqueta que arma la función `gsg`. */
export function etiquetaDelDia(fecha: string, ahora = new Date()): string {
  const deLima = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Lima', year: 'numeric', month: '2-digit', day: '2-digit' });
  if (fecha === deLima.format(ahora)) return 'Hoy';
  if (fecha === deLima.format(new Date(ahora.getTime() + 86_400_000))) return 'Mañana';
  const [y, m, d] = fecha.split('-').map(Number);
  const dia = new Intl.DateTimeFormat('es-PE', { timeZone: 'UTC', weekday: 'short' }).format(new Date(Date.UTC(y, m - 1, d, 12))).replace('.', '');
  return `${dia}, ${d}/${m}`;
}

/**
 * Lo mismo que `entregaElegible` para un courier sin API (20261012231500): los días salen de su operación, con la
 * misma función con la que la base acepta o rechaza el día elegido. null: la venta no va con un courier así.
 */
/** El registro como lo manda `fn_courier_entrega_elegible` (20261026160000): la misma forma que el de GSG, con la guía. */
type RegistroCrudo = {
  estado?: string | null; se_registra?: string | null; salida?: string | null; pago?: string | null; error?: string | null;
  enlace?: string | null; apertura?: string | null; fuera_de_hora?: boolean | null; guia?: string | null;
};
const ESTADOS_DEL_REGISTRO = new Set<EstadoDelRegistroEnElCourier['estado']>(['programado', 'registrando', 'registrado', 'fallido', 'anulando']);
export function registroDelCourier(r: RegistroCrudo | null | undefined): EstadoDelRegistroEnElCourier | null {
  if (!r || !r.estado || !ESTADOS_DEL_REGISTRO.has(r.estado as EstadoDelRegistroEnElCourier['estado'])) return null;
  return {
    estado: r.estado as EstadoDelRegistroEnElCourier['estado'], se_registra: r.se_registra ?? null, salida: r.salida ?? null,
    pago: r.pago ?? null, error: r.error ?? null, enlace: r.enlace ?? null, apertura: r.apertura ?? null, fuera_de_hora: r.fuera_de_hora === true,
  };
}

export async function entregaElegibleSinApi(ventaId: string): Promise<EntregaElegible> {
  const { data, error } = await supabase.rpc('fn_courier_entrega_elegible', { p_venta: ventaId });
  if (error) throw error;
  const d = (data ?? null) as {
    fechas?: string[]; corte?: string | null; entregar_el?: string | null; cobro_en_puerta?: CobroEnPuerta | null;
    apertura?: string | null; aperturas?: string[]; apertura_predeterminada?: string; comision_pos_pct?: number | null;
    registro?: RegistroCrudo | null;
  } | null;
  const codigos = new Set(d?.aperturas ?? []);
  return {
    fechas: (d?.fechas ?? []).map((fecha) => ({ fecha, etiqueta: etiquetaDelDia(fecha) })),
    dias: null, entregar_el: d?.entregar_el ?? null, cobro_en_puerta: d?.cobro_en_puerta ?? null,
    // 20261026160000: lo que se le mandó al courier (MERCourier, NX…), con la forma del registro de GSG.
    registro: registroDelCourier(d?.registro), faltan: [], se_registra: d?.registro?.se_registra ?? null,
    comision_pos_pct: typeof d?.comision_pos_pct === 'number' ? d.comision_pos_pct : null,
    apertura: d?.apertura ?? null,
    aperturas: CATALOGO_DE_APERTURAS.filter((a) => codigos.has(a.codigo)),
    apertura_predeterminada: d?.apertura_predeterminada ?? 'SEALED',
    corte: d?.corte ?? null, recojo: null, franja: null,
  };
}

export async function entregaElegible(ventaId: string): Promise<EntregaElegible> {
  const { data, error } = await supabase.functions.invoke('gsg', { body: { accion: 'fechas', order_id: ventaId } });
  if (error) throw new Error(await errorDeEdgeFunction(error, 'No se pudieron leer los días de entrega.'));
  // 🪤 El panel y la función se publican por separado: lo que una versión anterior de la función no trae,
  //    llega vacío y no rompe la pantalla.
  const d = data as Partial<EntregaElegible>;
  return {
    fechas: d.fechas ?? [], dias: d.dias ?? null,
    entregar_el: d.entregar_el ?? null, cobro_en_puerta: d.cobro_en_puerta ?? null,
    registro: d.registro ?? null, faltan: d.faltan ?? [], se_registra: d.se_registra ?? null,
    comision_pos_pct: typeof d.comision_pos_pct === 'number' ? d.comision_pos_pct : null,
    apertura: d.apertura ?? null, aperturas: d.aperturas ?? [], apertura_predeterminada: d.apertura_predeterminada ?? 'SEALED',
    // GSG (30-sep): hasta las 9:00 sale el mismo día; recoge de 10 a 12 y entrega de 2 a 8 de la tarde.
    corte: '9:00', recojo: 'de 10 a 12', franja: FRANJA_DE_ENTREGA,
  };
}

/**
 * La consulta de `entregaElegible` para la caché del panel: la MISMA para quien la pinta (`useEntregaElegida`) y para
 * quien la adelanta (`adelantarLaEntregaElegible`), así lo adelantado es lo que se pinta.
 */
export function consultaDeLaEntregaElegible(ventaId: string, via: ViaDeLaEntrega = 'api') {
  return {
    queryKey: [CLAVES_DROP.entregaElegible, ventaId, via] as const,
    queryFn: () => (via === 'sin_api' ? entregaElegibleSinApi(ventaId) : entregaElegible(ventaId)),
    staleTime: 30_000,
  };
}

/**
 * ⭐ LOS DÍAS DE GSG SE PIDEN CON EL PEDIDO, no cuando la tarjeta se dibuja. El founder (2-oct): «cuando cambio de
 *    página solo carga ubicación y tarifa en Entrega, y luego de 2 segundos recién cargan los demás datos». Era una
 *    fila: el pedido, después sus partes, después la página, y recién ahí la tarjeta pedía los días a la función `gsg`.
 *    Lo llama `OrderProvider` apenas sabe que el paquete va con GSG o con un courier sin API (North Express); si
 *    no, no pide nada.
 */
export function adelantarLaEntregaElegible(
  queryClient: QueryClient,
  venta: { id: string; courier_tenant_id?: string | null } | null | undefined,
  envios: readonly { carrier?: string | null }[],
): void {
  const via = viaDeLaEntrega(envios[0]?.carrier, venta?.courier_tenant_id);
  if (!venta || !via) return;
  void queryClient.prefetchQuery(consultaDeLaEntregaElegible(venta.id, via));
}

/**
 * El drop elige el día de la entrega y cómo paga el cliente, antes de registrar en el courier (20260930110000).
 * null = lo antes posible / efectivo. Registrado, ya no se cambia: se coordina con el courier.
 */
/** Si el cliente puede abrir el paquete al recibir (SEALED, INSPECT, TRY_ON); null = la predeterminada del courier. */
export async function elegirApertura(ventaId: string, apertura: string | null): Promise<void> {
  const { error } = await supabase.rpc('fn_pedido_elegir_apertura', { p_venta: ventaId, p_apertura: apertura });
  if (error) throw error;
}

/** Cómo eligió quien vende que pague el cliente («Método de pago»), para un courier que no lo trae de otro lado (Eva). */
export async function metodoDePagoElegido(ventaId: string): Promise<CobroEnPuerta | null> {
  const { data, error } = await supabase.from('order_delivery_profile').select('cobro_en_puerta').eq('order_id', ventaId).maybeSingle();
  if (error) throw error;
  return ((data as { cobro_en_puerta?: CobroEnPuerta | null } | null)?.cobro_en_puerta) ?? null;
}

export async function elegirEntrega(ventaId: string, entregarEl: string | null, cobro: CobroEnPuerta | null): Promise<void> {
  const { error } = await supabase.rpc('fn_pedido_elegir_entrega', { p_venta: ventaId, p_entregar_el: entregarEl, p_cobro: cobro });
  if (error) throw error;
}

export async function asignarMotorizado(orderId: string, motorizadoId: string): Promise<void> {
  const { error } = await supabase.rpc('fn_pedido_asignar_motorizado', { p_order: orderId, p_motorizado: motorizadoId });
  if (error) throw error;
}

// ── Sus tarifas por resultado (20260925172000) y las opciones de un pedido (20260925176000) ─────────
/**
 * Qué parte de su tarifa cobra el courier si el pedido no se entrega. Es lo que explica por qué en
 * Aliclik un anulado cuesta 9 y un no responde 4,50 con la misma tarifa de 13,50. La tarifa de cada
 * zona vive en sus zonas de reparto.
 */
export interface TarifasDelCourier {
  pct_rechazado: number;
  pct_no_responde: number;
  pct_anulado: number;
}

export async function tarifasDelCourier(courierId: string): Promise<TarifasDelCourier> {
  const { data, error } = await supabase.rpc('fn_courier_mis_tarifas', { p_courier: courierId });
  if (error) throw error;
  const t = (data ?? {}) as Partial<TarifasDelCourier>;
  return {
    pct_rechazado: Number(t.pct_rechazado ?? 100),
    pct_no_responde: Number(t.pct_no_responde ?? 50),
    pct_anulado: Number(t.pct_anulado ?? 50),
  };
}

export async function guardarTarifasDelCourier(courierId: string, t: TarifasDelCourier): Promise<void> {
  const { error } = await supabase.rpc('fn_courier_guardar_tarifas', {
    p_courier: courierId, p_pct_rechazado: t.pct_rechazado, p_pct_no_responde: t.pct_no_responde,
    p_pct_anulado: t.pct_anulado,
  });
  if (error) throw error;
}

/** Un courier para un pedido: cuánto cuesta ESE pedido (final) y si su destino está en la cobertura. */
export interface OpcionDeCourier {
  courier_id: string;
  courier: string;
  whatsapp: string | null;
  zona: string | null;
  tarifa: number;
  cubre: boolean;
  /**
   * No cubre porque la venta no tiene pin y el courier decide por su mapa (20261013120000): North Express cubre
   * La Molina a medias, y sin pin no se sabe de qué lado está. No es «fuera de su cobertura»: con el pin puede llegar.
   */
  falta_el_pin?: boolean;
  /** La zona roja del courier donde cae el pin (20261029010000): cubre, pero entrega coordinando. */
  coordinar?: string | null;
}

/** Por qué un courier no se puede elegir, en palabras de quien tiene que hacer algo al respecto. */
export function porQueNoCubre(o: Pick<OpcionDeCourier, 'falta_el_pin'>): string {
  return o.falta_el_pin ? 'Falta el pin para saber si llega' : 'Fuera de su cobertura';
}

export async function opcionesDeCourierParaElPedido(orderId: string): Promise<OpcionDeCourier[]> {
  const { data, error } = await supabase.rpc('fn_courier_opciones_del_pedido', { p_order: orderId });
  if (error) throw error;
  return ((data ?? []) as OpcionDeCourier[]).map((o) => ({ ...o, tarifa: Number(o.tarifa) }));
}

// ── La plata del contra entrega (20260925170000), día por día (20261026100000) ─────────────────
const ventasDelDia = (v: VentaDelDia[] | null | undefined): VentaDelDia[] =>
  (v ?? []).map((x) => ({ ...x, cobrado: Number(x.cobrado), tarifa: Number(x.tarifa), nota: x.nota ?? null }));

const diaPorRendir = (d: DiaPorRendir): DiaPorRendir => ({
  ...d, cobrado: Number(d.cobrado), tarifa: Number(d.tarifa), neto: Number(d.neto), a_favor: !!d.a_favor, ventas: ventasDelDia(d.ventas),
});

/** Un día del libro como lo manda la base: las ventas vienen en `detalle` y `ventas` es cuántas son. */
type DiaDelLibroCrudo = Omit<DiaDelLibro, 'ventas'> & { detalle: VentaDelLibro[] | null; ventas: number };

const diaDelLibro = ({ detalle, ...d }: DiaDelLibroCrudo): DiaDelLibro => ({
  dia: d.dia, estado: d.estado, a_favor: !!d.a_favor,
  cobrado: Number(d.cobrado), tarifa: Number(d.tarifa), pendiente: Number(d.pendiente),
  en_revision: Number(d.en_revision), liquidado: Number(d.liquidado),
  ventas: (detalle ?? []).map((v) => ({ ...ventasDelDia([v])[0], por_rendir: Number(v.por_rendir), estado: v.estado })),
  rendiciones: (d.rendiciones ?? []).map((r) => ({
    ...r, monto: Number(r.monto), dias: r.dias ?? [], depositos: (r.depositos ?? []).map((x) => ({ ...x, monto: Number(x.monto) })),
  })),
  cuadre: d.cuadre ?? null,
});

/** Un día del libro de un courier. Sólo lo ve Smarty. */
export interface DiaDelLibroDeUnCourier extends DiaDelLibro {
  courier_id: string;
  courier: string;
  whatsapp: string | null;
}

/**
 * El libro de cada courier, día por día (20261026100000): los últimos 45 días y, de antes, lo que todavía falta
 * liquidar; cada día con lo liquidado, lo que está en revisión y lo que falta, sus ventas y sus vouchers. Sólo Smarty.
 */
export async function libroDeLosCouriers(): Promise<DiaDelLibroDeUnCourier[]> {
  const { data, error } = await supabase.rpc('fn_courier_libro_por_dia');
  if (error) throw error;
  return ((data ?? []) as (DiaDelLibroCrudo & { courier_id: string; courier: string; whatsapp: string | null })[]).map((c) => ({
    ...diaDelLibro(c), courier_id: c.courier_id, courier: c.courier, whatsapp: c.whatsapp,
  }));
}

/**
 * Los pagos de los couriers que publican su plata por API (MERCourier), con su conciliación (20261028130000): si se
 * declararon solos, si no cuadran y por qué, y en qué quedó su rendición. Sólo Smarty (la base lo exige).
 */
export async function pagosDeLosCouriers(): Promise<PagoDelCourier[]> {
  const { data, error } = await supabase.rpc('fn_courier_liquidaciones_de_los_couriers');
  if (error) throw error;
  return ((data ?? []) as PagoDelCourier[]).map((p) => ({ ...p, monto: p.monto == null ? null : Number(p.monto), dias: p.dias ?? [] }));
}

/**
 * Un depósito o transferencia del courier a Smarty (20261013130000): su número de operación, el monto y el voucher.
 * Una rendición lleva uno o varios, y suman lo que el courier tiene por depositar.
 */
export interface DepositoDeRendicion {
  operacion: string;
  monto: number;
  comprobante_url: string;
}

/**
 * Smarty registra lo que depositó un courier que no tiene a nadie en Smarty, con sus depósitos, y lo confirma.
 * 🚨 Ya no hay «confirmar» a ciegas (20261013130000): sin depósitos con voucher no pasa nada a las billeteras.
 * ⭐ Por días (20261026100000): los que cubre el depósito; lo que está a favor del courier entra solo.
 */
export async function registrarRendicion(
  courierId: string, depositos: DepositoDeRendicion[], dias?: string[], decision?: string | null, nota?: string | null,
): Promise<{ ventas: number; recibido: number }> {
  const { data, error } = await supabase.rpc('fn_courier_registrar_rendicion', {
    p_courier: courierId, p_depositos: depositos, ...(dias ? { p_dias: dias } : {}),
    // 20261027130000: con diferencia, qué pasa con ella (sin diferencia, nada).
    ...(decision ? { p_decision: decision } : {}), ...(nota?.trim() ? { p_nota: nota.trim() } : {}),
  });
  if (error) throw error;
  laPlataCambio();
  return data as { ventas: number; recibido: number };
}

// ── La rendición del día (20260925180000) ──────────────────────────────────────────────────────
/** Una venta que el courier cobró (o intentó) y todavía no rindió. */
export interface VentaPorRendir {
  order_id: string;
  numero: string;
  tienda: string | null;
  cobrado: number;
  tarifa: number;
  /** Por qué la debe otra vez: un pago que no llegó (20260925186000). */
  nota: string | null;
  /** El día en que se entregó, en Lima (20261026100000). */
  dia: string;
  /** La rendición en revisión que la lleva, si ya se declaró. */
  rendicion: string | null;
  /** Su día no deja nada que depositar: está a favor del courier. */
  a_favor: boolean;
}

/** Una rendición que el courier declaró y Smarty todavía no revisó, con los días que cubre (20261026100000). */
export interface RendicionEnRevision {
  id: string;
  monto: number;
  /** Los días (de entrega, en Lima) de las ventas que cubre. */
  dias: string[];
  ventas: number;
  operacion: string;
  desde: string;
  depositos: DepositoDeRendicion[];
}

/** Una rendición rechazada cuyas ventas siguen por rendir: el courier tiene que saber por qué. */
export interface RendicionRechazada {
  id: string;
  dias: string[];
  motivo: string;
  revisado_en: string;
}

export interface MiRendicion {
  ventas: VentaPorRendir[];
  cobrado: number;
  tarifa: number;
  /** Lo cobrado menos su tarifa, de todo lo que tiene por rendir (declarado o no). */
  a_depositar: number;
  /** Lo libre por rendir, día por día (20261026100000). */
  dias: DiaPorRendir[];
  /** Cada rendición en revisión, con sus días. */
  declaradas: RendicionEnRevision[];
  /** Lo que Smarty le debe (días sin cobros): se descuenta en la próxima rendición. */
  a_favor: number;
  /** Las cuentas de Smarty donde se deposita. */
  cuentas: { bank?: string; number?: string; cci?: string; holder?: string }[];
  /** La primera en revisión (la clave de antes de 20261026100000). */
  declarada: RendicionEnRevision | null;
  /** El último rechazo vigente (la clave de antes de 20261026100000). */
  rechazo: string | null;
  /** Los rechazos cuyas ventas siguen por rendir, el más nuevo primero. */
  rechazos: RendicionRechazada[];
  /** Su libro día por día: los últimos 45 días y lo de antes que falta liquidar, el más nuevo primero. */
  libro: DiaDelLibro[];
}

const enRevision = (r: RendicionEnRevision): RendicionEnRevision => ({
  ...r, monto: Number(r.monto), dias: r.dias ?? [], depositos: (r.depositos ?? []).map((x) => ({ ...x, monto: Number(x.monto) })),
});

export async function miRendicion(courierId: string): Promise<MiRendicion> {
  const { data, error } = await supabase.rpc('fn_courier_mi_rendicion', { p_courier: courierId });
  if (error) throw error;
  const d = data as MiRendicion;
  return {
    ...d,
    ventas: (d.ventas ?? []).map((v) => ({ ...v, cobrado: Number(v.cobrado), tarifa: Number(v.tarifa) })),
    cobrado: Number(d.cobrado), tarifa: Number(d.tarifa), a_depositar: Number(d.a_depositar),
    dias: (d.dias ?? []).map(diaPorRendir),
    declaradas: (d.declaradas ?? []).map(enRevision),
    a_favor: Number(d.a_favor ?? 0),
    cuentas: d.cuentas ?? [],
    declarada: d.declarada ? enRevision(d.declarada) : null,
    rechazos: (d.rechazos ?? []).map((r) => ({ ...r, dias: r.dias ?? [] })),
    libro: ((d.libro ?? []) as unknown as DiaDelLibroCrudo[]).map(diaDelLibro),
  };
}

/**
 * «Ya deposité»: el courier declara cada depósito con su voucher, por los días que cubre (20261026100000). Declarar no
 * mueve plata; la rendición se hace cuando Smarty confirma. La base exige que sumen lo que hay por depositar en esos
 * días, con lo que está a favor del courier ya descontado (20261013130000).
 */
export async function declararRendicion(courierId: string, depositos: DepositoDeRendicion[], dias?: string[]): Promise<void> {
  const { error } = await supabase.rpc('fn_courier_declarar_rendicion', {
    p_courier: courierId, p_depositos: depositos, ...(dias ? { p_dias: dias } : {}),
  });
  if (error) throw error;
  laPlataCambio();
}

export interface RendicionDeclarada {
  id: string;
  courier_id: string;
  courier: string;
  monto: number;
  ventas: number;
  operacion: string;
  comprobante_url: string | null;
  creado_en: string;
  /** Cada depósito con su voucher (20261013130000). */
  depositos: DepositoDeRendicion[];
  /** Los días que cubre (20261026100000). */
  dias: string[];
}

export async function rendicionesDeclaradas(): Promise<RendicionDeclarada[]> {
  const { data, error } = await supabase.rpc('fn_courier_rendiciones_declaradas');
  if (error) throw error;
  return ((data ?? []) as RendicionDeclarada[]).map((r) => ({
    ...r, monto: Number(r.monto), dias: r.dias ?? [], depositos: (r.depositos ?? []).map((x) => ({ ...x, monto: Number(x.monto) })),
  }));
}

/** Una rendición del historial (20261013180000): lo que el courier le pagó a Smarty, con sus depósitos y en qué quedó. */
export interface RendicionPasada {
  id: string;
  courier_id: string;
  courier: string;
  estado: 'declarada' | 'confirmada' | 'rechazada';
  monto: number;
  /** Los números de las ventas que cubre. */
  ventas: string[];
  depositos: DepositoDeRendicion[];
  creado_en: string;
  revisado_en: string | null;
  motivo_rechazo: string | null;
  /** Los días que cubre (20261026100000). */
  dias: string[];
}

/** El historial de rendiciones: las de un courier (él o Smarty), o las de todos (sólo Smarty) si no se dice cuál. */
export async function historialDeRendiciones(courierId?: string): Promise<RendicionPasada[]> {
  const { data, error } = await supabase.rpc('fn_courier_historial_de_rendiciones', courierId ? { p_courier: courierId } : {});
  if (error) throw error;
  return ((data ?? []) as RendicionPasada[]).map((r) => ({
    ...r, monto: Number(r.monto), ventas: r.ventas ?? [], dias: r.dias ?? [],
    depositos: (r.depositos ?? []).map((x) => ({ ...x, monto: Number(x.monto) })),
  }));
}

export async function confirmarRendicionDeclarada(rendicionId: string, decision?: string | null, nota?: string | null): Promise<{ ventas: number; recibido: number }> {
  const { data, error } = await supabase.rpc('fn_courier_confirmar_rendicion', {
    p_rendicion: rendicionId,
    // 20261027130000: con diferencia, qué pasa con ella (sin diferencia, nada).
    ...(decision ? { p_decision: decision } : {}), ...(nota?.trim() ? { p_nota: nota.trim() } : {}),
  });
  if (error) throw error;
  laPlataCambio();
  return data as { ventas: number; recibido: number };
}

export async function rechazarRendicion(rendicionId: string, motivo: string): Promise<void> {
  const { error } = await supabase.rpc('fn_courier_rechazar_rendicion', { p_rendicion: rendicionId, p_motivo: motivo });
  if (error) throw error;
  laPlataCambio();
}

// ── Los cobros que el cliente le pagó a Smarty al recibir (20260925181000) ──────────────────────
/** Una venta que el cliente pagó por transferencia o POS: Smarty la confirma cuando ve la plata. */
export interface CobroPorConfirmar {
  order_id: string;
  numero: string;
  tienda: string | null;
  courier: string | null;
  medio: 'transferencia' | 'pos';
  operacion: string | null;
  /** Lo que tiene que llegar al banco de Smarty (del POS, lo cobrado menos su comisión). */
  monto: number;
  fecha: string;
}

export async function cobrosPorConfirmar(): Promise<CobroPorConfirmar[]> {
  const { data, error } = await supabase.rpc('fn_drop_cobros_por_confirmar');
  if (error) throw error;
  return ((data ?? []) as CobroPorConfirmar[]).map((c) => ({ ...c, monto: Number(c.monto) }));
}

export async function confirmarCobro(orderId: string): Promise<void> {
  const { error } = await supabase.rpc('fn_drop_confirmar_cobro', { p_order: orderId });
  if (error) throw error;
  laPlataCambio();
}

/** La plata nunca llegó (20260925186000): la debe el courier, que aceptó el pago sin verlo. */
export async function cobroNoLlego(orderId: string, motivo: string): Promise<void> {
  const { error } = await supabase.rpc('fn_drop_cobro_no_llego', { p_order: orderId, p_motivo: motivo });
  if (error) throw error;
  laPlataCambio();
}
